import { useState } from 'react'
import PropTypes from 'prop-types'
import { Alert, FileInput, FileListItem, Hint, Modal, PrimaryButton, Text, TextButton } from '@artprov/design-system'
import {
  ACCEPT_DDJJ,
  ESTILOS_MODAL_DDJJ,
  EXTENSIONES_DDJJ,
  MAX_DDJJ_BYTES,
  MODAL_CARGA_DDJJ,
} from './constants/modal-carga-ddjj.jsx'
import { validarArchivoDdjj, varianteHint } from './utils/validar-archivo-ddjj.js'
import { useCargaDdjj } from './hooks/useCargaDdjj.js'
import { useDescargaTemplateDdjj } from './hooks/useDescargaTemplateDdjj.js'

// PBI 81147: modal de carga de la DDJJ. Se usa la forma compuesta del Modal porque el
// AcceptButton del simple no acepta disabled (criterios 9, 10 y 14).
// Textos y estilos son fijos: MODAL_CARGA_DDJJ y ESTILOS_MODAL_DDJJ, compartidos por
// todas las aperturas (banner y Mis cuentas). Las reglas de validación sí son configurables
// vía la prop `validacion`: sin prop se aplican las de la carga del médico (5 MB, imágenes + PDF).
// Las variantes de los Hint no se configuran: las define la validación del archivo.
export default function ModalDdjj({ open, onClose, contrato, onCargada, validacion }) {
  const T = MODAL_CARGA_DDJJ
  const E = ESTILOS_MODAL_DDJJ
  const [archivo, setArchivo] = useState(null)
  // D8: sin `contrato` los hooks usan el contrato activo (apertura desde el banner).
  // Validación configurable (develop): sin `validacion` se aplican las reglas de la carga del médico.
  const maxBytes = validacion?.maxBytes ?? MAX_DDJJ_BYTES
  const extensiones = validacion?.extensiones ?? EXTENSIONES_DDJJ
  const accept = validacion?.extensiones?.join(',') ?? ACCEPT_DDJJ
  const { formatoOk, tamanioOk, valido } = validarArchivoDdjj(archivo, {
    maxBytes,
    extensiones,
  })

  // Con éxito no hay request que abortar ni error que limpiar: sólo se resetea y se cierra.
  const { enviar, abortar, enviando, error, limpiarError } = useCargaDdjj({
    contrato,
    onExito: () => {
      setArchivo(null)
      onClose()
    },
    onCargada,
    mensajeError: T.errorGenerico,
    mensajeSesionVencida: T.sesionVencida,
    mensajeExito: T.exito,
  })

  // P1 resuelta: el template lo genera reporting (PERFIL_MEDICO_DDJJ) vía clientes_service.
  const {
    descargar,
    descargando,
    error: errorDescarga,
    limpiarError: limpiarErrorDescarga,
  } = useDescargaTemplateDdjj({
    contrato,
    nombreArchivo: T.template.archivo,
    mensajeError: T.errorDescarga,
  })

  const cerrar = () => {
    abortar()
    setArchivo(null)
    limpiarError()
    limpiarErrorDescarga()
    onClose()
  }

  const cambiarArchivo = (nuevo) => {
    limpiarError()
    setArchivo(nuevo)
  }

  return (
    <Modal.Container open={open} setOpen={(abierto) => !abierto && cerrar()}>
      <Modal.Content {...E.contenido}>
        <Modal.Header>
          <Modal.Title>{T.titulo}</Modal.Title>
          <Modal.Close onClose={cerrar} />
        </Modal.Header>

        {/* pb-3: deja espacio para la sombra del último Alert, que si no queda cortada por el footer. */}
        <Modal.Body classNameScrollContainer="m-0 pb-3">
          <div className="flex flex-col gap-4">
            <Text {...E.bajada}>{T.bajada}</Text>
            <Alert
              {...E.alerta}
              description={<span className="whitespace-pre-line">{T.alerta}</span>}
              isInteractive={false}
            />
            {/* Fila del template con FileListItem del DS (mockup: ícono, nombre, badge PDF, link Descargar) */}
            <FileListItem
              {...E.filaTemplate}
              files={[
                {
                  fileName: T.template.nombre,
                  fileType: T.template.tipo.toLowerCase(),
                  content: descargando ? T.template.descargando : T.template.link,
                },
              ]}
              variant="link"
              onAction={descargar}
              isDisabled={descargando}
            />
            {errorDescarga ? <Alert {...E.alertaError} description={errorDescarga} isInteractive={false} /> : null}

            <div className="flex flex-col gap-1">
              <Text {...E.labelArchivo} as="label" htmlFor="archivo-ddjj">
                {T.labelArchivo}
              </Text>
              <FileInput
                id="archivo-ddjj"
                name="archivo"
                accept={accept}
                placeholder={T.placeholder}
                value={archivo}
                onChange={cambiarArchivo}
                isInvalid={archivo !== null && !valido}
                isDisabled={enviando}
                aria-describedby="hint-formato-ddjj hint-tamanio-ddjj"
              />
              <Hint id="hint-formato-ddjj" variant={varianteHint(formatoOk)}>
                {T.hintFormato}
              </Hint>
              <Hint id="hint-tamanio-ddjj" variant={varianteHint(tamanioOk)}>
                {T.hintTamanio}
              </Hint>
            </div>

            {/* description en lugar de title: el title del DS se pinta en negrita y el error
                de sesión vencida debe verse como texto normal, igual que el resto de los avisos. */}
            {error ? <Alert {...E.alertaError} description={error} isInteractive={false} /> : null}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <TextButton {...E.botonCancelar} onClick={cerrar}>
            {T.cancelar}
          </TextButton>
          <PrimaryButton {...E.botonCargar} disabled={!valido || enviando} onClick={() => enviar(archivo)}>
            {enviando ? T.cargando : T.cargar}
          </PrimaryButton>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Container>
  )
}

ModalDdjj.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // D8: contrato de la fila de Mis cuentas; sin prop, el contrato activo.
  contrato: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  // Se llama después de un 201 o 409 para que el origen refresque sus datos.
  onCargada: PropTypes.func,
  // Reglas de validación opcionales; sin prop, las de la carga del médico (5 MB, imágenes + PDF).
  validacion: PropTypes.shape({
    maxBytes: PropTypes.number,
    extensiones: PropTypes.arrayOf(PropTypes.string),
  }),
}
